import React, { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Progress, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import GeneralLedgerService from "../../../Services/generalLedger/GeneralLedgerService";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import { useAuth } from "../../../_helper/Auth/AuthProvider";
import { formatSize, readBlobError, saveBlobResponse } from "./glUtils";

const ACCEPT = "application/pdf,image/jpeg,image/png";
const DEFAULT_MAX_KB = 10 * 1024;

export default function EntryAttachmentsPanel({ entryId, canAttach, isAdmin = false, maxKb }) {
  const queryClient = useQueryClient();
  const { userData } = useAuth();
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);
  const maxBytes = (Number(maxKb) || DEFAULT_MAX_KB) * 1024;

  const attachmentsQuery = useQuery({
    queryKey: ["glAttachments", entryId],
    queryFn: () => GeneralLedgerService.getAttachments(entryId).then((r) => r.data),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["glAttachments", entryId] });
    queryClient.invalidateQueries({ queryKey: ["glEntries"] });
  };

  const uploadMutation = useMutation({
    mutationFn: (file) => GeneralLedgerService.uploadAttachment(entryId, file, setProgress),
    onSuccess: () => {
      toast.success("Attachment uploaded");
      refresh();
    },
    onError: (e) => toast.error(getApiErrorMessage(e, "Upload failed.")),
    onSettled: () => setProgress(null),
  });

  const deleteMutation = useMutation({
    mutationFn: (attachmentId) => GeneralLedgerService.deleteAttachment(attachmentId),
    onSuccess: () => {
      toast.success("Attachment deleted");
      refresh();
    },
    onError: (e) => toast.error(getApiErrorMessage(e, "Could not delete the attachment.")),
  });

  const onFile = (event) => {
    const file = event.target.files?.[0];
    // Cleared up front so picking the same file again (after a rejection or upload) still fires onChange.
    event.target.value = "";
    if (!file) return;
    if (!ACCEPT.split(",").includes(file.type)) {
      toast.error("Only PDF, JPG and PNG files are accepted.");
      return;
    }
    if (file.size > maxBytes) {
      toast.error(`File is larger than ${formatSize(maxBytes)}.`);
      return;
    }
    uploadMutation.mutate(file);
  };

  const download = async (attachment) => {
    try {
      const response = await GeneralLedgerService.downloadAttachment(attachment.id);
      saveBlobResponse(response, attachment.original_name);
    } catch (e) {
      toast.error((await readBlobError(e)) || "Download failed.");
    }
  };

  const confirmDelete = (attachment) => {
    if (window.confirm(`Delete "${attachment.original_name}"? This cannot be undone.`)) {
      deleteMutation.mutate(attachment.id);
    }
  };

  const items = attachmentsQuery.data || [];

  return (
    <div className="gl-panel">
      <h6 className="gl-panel-title">Attachments</h6>
      {attachmentsQuery.isLoading && <Spinner size="sm" />}
      {attachmentsQuery.isError && (
        <p className="text-danger small">{getApiErrorMessage(attachmentsQuery.error, "Could not load attachments.")}</p>
      )}
      {!attachmentsQuery.isLoading && !attachmentsQuery.isError && items.length === 0 && <p className="gl-muted">No files attached.</p>}
      <ul className="gl-attachment-list">
        {items.map((a) => (
          <li key={a.id}>
            <Button color="link" className="p-0" onClick={() => download(a)}>
              {a.original_name}
            </Button>
            <span className="gl-muted ms-2">
              {formatSize(a.size)} · {a.uploaded_by?.name || ""}
            </span>
            {canAttach && (isAdmin || a.uploaded_by?.id === userData?.id) && (
              <Button
                color="link"
                size="sm"
                className="text-danger p-0 ms-2"
                disabled={deleteMutation.isPending}
                onClick={() => confirmDelete(a)}
              >
                Delete
              </Button>
            )}
          </li>
        ))}
      </ul>
      {canAttach && (
        <>
          <input ref={inputRef} type="file" accept={ACCEPT} hidden onChange={onFile} />
          <Button
            color="outline-primary"
            size="sm"
            disabled={uploadMutation.isPending}
            onClick={() => inputRef.current?.click()}
          >
            {uploadMutation.isPending ? "Uploading…" : "Attach file"}
          </Button>
          {progress !== null && <Progress className="mt-2" value={progress}>{progress}%</Progress>}
        </>
      )}
    </div>
  );
}
